import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Plus, CheckSquare, Clock, Trash2, User, ChevronRight } from 'lucide-react';

export function ProjectTasksTab({
  tasks, isAdmin, isStaff, onAddTask, onStatusChange, onDeleteTask,
  formatDate, getPriorityColor, getAssigneeNames,
}) {
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Project Tasks</h3>
        {(isAdmin || isStaff) && (
          <Button onClick={onAddTask} className="btn-primary" data-testid="add-task-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Task
          </Button>
        )}
      </div>

      {tasks.length === 0 ? (
        <Card>
          <CardContent className="empty-state py-12">
            <CheckSquare className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No tasks yet</h3>
            <p className="text-sm text-muted-foreground">
              Add tasks to track progress on this project
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => {
            const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';
            const assigneeText = getAssigneeNames(task);
            return (
              <Card key={task.task_id} className="overflow-hidden" data-testid={`task-row-${task.task_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Main content */}
                    <div className="flex-1 min-w-0">
                      {/* Title + Priority */}
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="font-medium text-sm leading-snug">{task.title}</p>
                        <span className={`status-badge shrink-0 ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                      </div>

                      {/* Description - full text visible */}
                      {task.description && (
                        <p className="text-sm text-muted-foreground mb-2 leading-relaxed">
                          {task.description}
                        </p>
                      )}

                      {/* Meta row: date + assignees */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                        <span className={`flex items-center gap-1 ${isOverdue ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                          <Clock className="h-3 w-3" />
                          {formatDate(task.due_date)}
                        </span>
                        {assigneeText && assigneeText !== '-' && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <User className="h-3 w-3" />
                            {assigneeText}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions row */}
                  {(isAdmin || isStaff) && (
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                      <Select
                        value={task.status}
                        onValueChange={(value) => onStatusChange(task.task_id, value)}
                      >
                        <SelectTrigger className="w-32 h-8 text-xs" data-testid={`task-status-${task.task_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todo">To Do</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive h-8 w-8"
                        onClick={() => onDeleteTask(task.task_id)}
                        data-testid={`delete-task-${task.task_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
